import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";

import { ApiContext } from "../../../App";



export default function HvacEditPopUp(props) {
	const { open, onCloseWithoutSaving, onCloseWithUpdate, floorPlans, points, tariffs } = props;

	const { t } = useTranslation();

	const [data, setData] = React.useState(props.data);
	const [errorMessages, setErrorMessages] = React.useState({});
	

	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

	const {hvacApi} = React.useContext(ApiContext)

	React.useEffect(() => {
		setData(props.data);
	}, [props.data]);

	const handleCloseWithoutSaving = () => {
		setData(props.data);
		onCloseWithoutSaving();
		setErrorMessages({});
	};

	const handleSave = () => {
		setErrorMessages({});

		const onSuccess = (result) => {
			onCloseWithUpdate(result);
		};

		const onError = (response) => {

			console.log(response)

			setErrorMessages(response);

			const non_field_errors = Object.entries(response)
				.filter(([key]) => key === "non_field_errors")
				.map(([key, value]) => value);
			if (non_field_errors.length > 0) {
				window.alert(non_field_errors.join("; "));
			}
		};

		if (!data.hasOwnProperty("url")) {
			hvacApi.create(data, onSuccess, onError);
		} else {
			hvacApi.update(data.url, data, onSuccess, onError);
		}
	};

	return (
		<React.Fragment>
			<Dialog
				open={open}
				onClose={handleCloseWithoutSaving}
				fullScreen={fullScreen} fullWidth maxWidth={"md"}
			>
				<DialogTitle>{t("Edit hvac")}</DialogTitle>
				<DialogContent>
					<Stack spacing={2}>
						<TextField
							variant="standard"
							id="location"
							label={t("Location")}
							value={data.location ?? ""}
							onChange={(event)=>setData({...data, location: event.target.value})}
							error={errorMessages.hasOwnProperty("location")}
							helperText={errorMessages["location"] ? errorMessages["location"] : ""}
						/>	
						<TextField
							variant="standard"
							id="name"
							label={t("Name")}
							value={data.name ?? ""}
							onChange={(event)=>setData({...data, name: event.target.value})}
							error={errorMessages.hasOwnProperty("name")}
							helperText={errorMessages["name"] ? errorMessages["name"] : ""}
						/>	
						<TextField
							variant="standard"
							id="set_point"
							label={t("Set point")}
							onChange={(event)=>setData({...data, set_point: event.target.value})}
							value={data.set_point ?? ""}
							select
							helperText={
								errorMessages["set_point"] ? errorMessages["set_point"] : ""
							}
							error={errorMessages.hasOwnProperty("set_point")}
						>
							{
								<MenuItem key={"none"} value={""}>
									{t("None")}
								</MenuItem>
							}
							{points.filter((item)=>(item.category === "OUTPUT") && (item.key === "SET_POINT"))
							.toSorted(
								(a, b)=> (a.location + a.system + a.name + a.identifier) > (b.location + b.system + b.name + b.identifier) ? 1 : -1
							).map((option) => (
								<MenuItem
									key={option.url}
									value={option.url}
								>
									{option.location} | {option.system} | {option.name} ({option.identifier})
								</MenuItem>
							))}
						</TextField>
						<TextField
							variant="standard"
							id="on_off"
							label={t("On/off")}
							onChange={(event)=>setData({...data, on_off: event.target.value})}
							value={data.on_off ?? ""}
							select
							helperText={
								errorMessages["on_off"] ? errorMessages["on_off"] : ""
							}
							error={errorMessages.hasOwnProperty("on_off")}
						>
							{
								<MenuItem key={"none"} value={""}>
									{t("None")}
								</MenuItem>
							}
							{points.filter((item)=>(item.category === "OUTPUT") && (item.key === "ON_OFF"))
							.toSorted(
								(a, b)=> (a.location + a.system + a.name + a.identifier) > (b.location + b.system + b.name + b.identifier) ? 1 : -1
							).map((option) => (
								<MenuItem
									key={option.url}
									value={option.url}
								>
									{option.location} | {option.system} | {option.name} ({option.identifier})
								</MenuItem>
							))}
						</TextField>
						<TextField
							variant="standard"
							id="mode"
							label={t("Mode")}
							onChange={(event)=>setData({...data, mode: event.target.value})}
							value={data.mode ?? ""}
							select
							helperText={
								errorMessages["mode"] ? errorMessages["mode"] : ""
							}
							error={errorMessages.hasOwnProperty("mode")}
						>
							{
								<MenuItem key={"none"} value={""}>
									{t("None")}
								</MenuItem>
							}
							{points.filter((item)=>(item.category === "OUTPUT") && (item.key === "MODE"))
							.toSorted(
								(a, b)=> (a.location + a.system + a.name + a.identifier) > (b.location + b.system + b.name + b.identifier) ? 1 : -1
							).map((option) => (
								<MenuItem
									key={option.url}
									value={option.url}
								>
									{option.location} | {option.system} | {option.name} ({option.identifier})
								</MenuItem>
							))}
						</TextField>
						<TextField
							variant="standard"
							id="fan_speed"
							label={t("Fan speed")}
							onChange={(event)=>setData({...data, fan_speed: event.target.value})}
							value={data.fan_speed ?? ""}
							select
							helperText={
								errorMessages["fan_speed"] ? errorMessages["fan_speed"] : ""
							}
							error={errorMessages.hasOwnProperty("fan_speed")}
						>
							{
								<MenuItem key={"none"} value={""}>
									{t("None")}
								</MenuItem>
							}
							{points.filter((item)=>(item.category === "OUTPUT") && (item.key === "FAN_SPEED"))
							.toSorted(
								(a, b)=> (a.location + a.system + a.name + a.identifier) > (b.location + b.system + b.name + b.identifier) ? 1 : -1
							).map((option) => (
								<MenuItem
									key={option.url}
									value={option.url}
								>
									{option.location} | {option.system} | {option.name} ({option.identifier})
								</MenuItem>
							))}
						</TextField>
						<TextField
							variant="standard"
							id="temperature"
							label={t("Temperature")}
							onChange={(event)=>setData({...data, temperature: event.target.value})}
							value={data.temperature ?? ""}
							select
							helperText={
								errorMessages["temperature"] ? errorMessages["temperature"] : ""
							}
							error={errorMessages.hasOwnProperty("temperature")}
						>
							{
								<MenuItem key={"none"} value={""}>
									{t("None")}
								</MenuItem>
							}
							{points.filter((item)=>(item.category === "INPUT") && (item.key === "TEMPERATURE"))
							.toSorted(
								(a, b)=> (a.location + a.system + a.name + a.identifier) > (b.location + b.system + b.name + b.identifier) ? 1 : -1
							).map((option) => (
								<MenuItem
									key={option.url}
									value={option.url}
								>
									{option.location} | {option.system} | {option.name} ({option.identifier})
								</MenuItem>
							))}
						</TextField>
						<TextField
							variant="standard"
							id="occupancy"
							label={t("Occupancy")}
							onChange={(event)=>setData({...data, occupancy: event.target.value})}
							value={data.occupancy ?? ""}
							select
							helperText={
								errorMessages["occupancy"] ? errorMessages["occupancy"] : ""
							}
							error={errorMessages.hasOwnProperty("occupancy")}
						>
							{
								<MenuItem key={"none"} value={""}>
									{t("None")}
								</MenuItem>
							}
							{points.filter((item)=>(item.category === "INPUT") && (["MOTION_START_END", "OCCUPIED"].includes(item.key)))
							.toSorted(
								(a, b)=> (a.location + a.system + a.name + a.identifier) > (b.location + b.system + b.name + b.identifier) ? 1 : -1
							).map((option) => (
								<MenuItem
									key={option.url}
									value={option.url}
								>
									{option.location} | {option.system} | {option.name} ({option.identifier})
								</MenuItem>
							))}
						</TextField>
                        <TextField
                            variant="standard"
                            id="tariff"
                            label={t("Tariff")}
                            onChange={(event)=>setData({...data, tariff: event.target.value})}
                            value={data.tariff ?? ""}
                            select
                            helperText={
                                errorMessages["tariff"] ? errorMessages["tariff"] : ""
                            }
                            error={errorMessages.hasOwnProperty("tariff")}
                        >
                            {tariffs.map((option) => (
                                    <MenuItem
                                        key={option.url}
                                        value={option.url}
                                    >
                                        {option.name}
                                    </MenuItem>
                                ))}
                        </TextField>
						<TextField
							variant="standard"
							id="insulation"
							label={t("Insulation")}
							onChange={(event)=>setData({...data, insulation: event.target.value})}
							value={data.insulation ?? ""}
							select
							helperText={
								errorMessages["insulation"] ? errorMessages["insulation"] : ""
							}
							error={errorMessages.hasOwnProperty("insulation")}
						>
							<MenuItem value={"LOW"}>
								{t("Low")}
							</MenuItem>
							<MenuItem value={"REGULAR"}>
								{t("Regular")}
							</MenuItem>
							<MenuItem value={"HIGH"}>
								{t("High")}
							</MenuItem>
						</TextField>
						<TextField
							variant="standard"
							id="capacity"
							label={t("Capacity")}
							onChange={(event)=>setData({...data, capacity: event.target.value})}
							value={data.capacity ?? ""}
							select
							helperText={
								errorMessages["capacity"] ? errorMessages["capacity"] : ""
							}
							error={errorMessages.hasOwnProperty("capacity")}
						>
							<MenuItem value={"LOW"}>
								{t("Low")}
							</MenuItem>
							<MenuItem value={"REGULAR"}>
								{t("Regular")}
							</MenuItem>
							<MenuItem value={"HIGH"}>
								{t("High")}
							</MenuItem>
						</TextField>
                        <TextField
                            variant="standard"
                            id="average_power_consumption_kw"
                            label={t("Average power consumption (kW)")}
                            value={data.average_power_consumption_kw ?? ""}
                            onChange={(event)=>setData({...data, average_power_consumption_kw: event.target.value})}
                            error={errorMessages.hasOwnProperty("average_power_consumption_kw")}
                            helperText={errorMessages["average_power_consumption_kw"] ? errorMessages["average_power_consumption_kw"] : ""}
                        />
                        <TextField
                            variant="standard"
                            id="preheat_padding_hours"
                            label={t("Baseline preheat hours")}
                            value={data.preheat_padding_hours ?? ""}
                            onChange={(event)=>setData({...data, preheat_padding_hours: event.target.value})}
                            error={errorMessages.hasOwnProperty("preheat_padding_hours")}
                            helperText={errorMessages["preheat_padding_hours"] ? errorMessages["preheat_padding_hours"] : ""}
                        />
                        <TextField
							variant="standard"
							id="floor_plan"
							label={t("Floor plan")}
							onChange={(event)=>setData({...data, floor_plan: event.target.value})}
							value={data.floor_plan ?? ""}
							select
							helperText={
								errorMessages["floor_plan"] ? errorMessages["floor_plan"] : ""
							}
							error={errorMessages.hasOwnProperty("floor_plan")}
						>
							{floorPlans.map((option) => (
								<MenuItem
									key={option.url}
									value={option.url}
								>
									{option.name}
								</MenuItem>
							))}
							{
								<MenuItem key={"none"} value={""}>
									{t("None")}
								</MenuItem>
							}
						</TextField>
						<FormControlLabel
								control={
									<Checkbox
										id="display_on_floor_plan"
										checked={data.display_on_floor_plan ?? false}
										onChange={(event)=>setData({...data, display_on_floor_plan: event.target.checked})}
										style={{ paddingLeft: 0, paddingRight: 3 }}
									/>
								}
								label={t("Display on floor plan")}
						/>
						<FormControlLabel
							control={
								<Checkbox
									id="automated"
									checked={data.automated ?? false}
									onChange={(event)=>setData({...data, automated: event.target.checked})}
									style={{ paddingLeft: 0, paddingRight: 3 }}
								/>
							}
							label={t("Automated")}
						/>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    id="auto_turn_off_enabled"
                                    checked={data.auto_turn_off_enabled ?? false}
                                    onChange={(event)=>setData({...data, auto_turn_off_enabled: event.target.checked})}
                                    style={{ paddingLeft: 0, paddingRight: 3 }}
                                />
                            }
                            label={t("Auto turn off")}
                        />
                        <TextField
                            variant="standard"
                            id="auto_turn_off_time"
                            label={t("Turn off time")}
                            value={data.auto_turn_off_time ?? "00:00"}
                            onChange={(event)=>setData({...data, auto_turn_off_time: event.target.value})}
                            error={errorMessages.hasOwnProperty("auto_turn_off_time")}
                            helperText={errorMessages["auto_turn_off_time"] ? errorMessages["auto_turn_off_time"] : ""}
                        />
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseWithoutSaving} autoFocus>
						{t("Cancel")}
					</Button>
					<Button onClick={handleSave}>{t("Save")}</Button>
				</DialogActions>
			</Dialog>
		</React.Fragment>
	);
};
